import classNames from "classnames";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  useExpanded,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";

import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import DocumentsColumns from "./DocumentsColumns";
import DocumentsFilters from "./DocumentsFilters";
import AdminPagination from "../casesTable/Pagination";
import DocumentsPanel from "../documentsPanel/DocumentsPanel";
import OrdersTableView from "../ordersTable/OrdersTableView";

const FilterableDocumentsTable = ({ columns }: { columns: any[] }) => {
  const location = useLocation();
  const {
    results: documents,
    count,
    next,
    loading: isDocumentsLoading,
  } = useSelector(({ admin }: ApplicationState) => admin.documents);
  const documentQuery = useParams<{ document_id: string }>().document_id;
  const document_id = parseInt(documentQuery || "0");

  const usp = new URLSearchParams(location.search);

  const initialParams = useMemo(() => {
    const sortBy = usp.get("sortBy");
    return {
      sortBy: sortBy
        ? sortBy.split(",").map((s) => ({
            id: s[0] === "-" ? s.slice(1) : s,
            desc: s[0] === "-",
          }))
        : [],
      pageIndex: parseInt(usp.get("page") || "1") - 1,
    };
  }, [location]);

  const setPageSizeToStorage = (value: string) => {
    localStorage.setItem("AdminDocumentsPageSize", value);
  };

  const {
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,

    // Pagination
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state: { sortBy, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: documents || [],
      autoResetExpanded: false,
      manualSortBy: true,
      manualPagination: true,
      pageCount: -1,
      defaultColumn: {
        sortDescFirst: true,
      },
      initialState: {
        ...initialParams,
        pageSize: Number(
          JSON.parse(localStorage.getItem("AdminDocumentsPageSize") || "25")
        ),
      },
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const pageCount = count ? Math.ceil(count / pageSize) : -1;

  return (
    <div className="AdminCaselist">
      {document_id || documentQuery === "upload" ? (
        <DocumentsPanel
          documentId={document_id}
          isUploading={documentQuery === "upload"}
          returnUrl={`/admin/documents${location.search}`}
        />
      ) : undefined}
      <DocumentsFilters
        sortBy={sortBy}
        pageIndex={pageIndex}
        pageSize={pageSize}
        setPage={gotoPage}
      />
      <div
        className={classNames({ "AdminCaselist--loading": isDocumentsLoading })}
      >
        {isDocumentsLoading && (
          <div className="AdminCaselist__spinner">
            <Spinner />
          </div>
        )}
        <AdminPagination
          {...{
            nextPage,
            previousPage,
            gotoPage,
            canPreviousPage,
            canNextPage: canNextPage && !!next,
            totalCount: count,

            pageIndex,
            pageCount,
            pageSize,
            setPageSize,

            toggleAllRowsExpanded,

            label: "documents",
            setToLocalStorage: setPageSizeToStorage,
            usePageSizeSelect: false,
            idSuffix: "upper",
          }}
        />
        <div className="adminList__container">
          <OrdersTableView
            headerGroups={headerGroups}
            rows={rows}
            prepareRow={prepareRow}
          />
        </div>
        <AdminPagination
          {...{
            nextPage,
            previousPage,
            gotoPage,
            canPreviousPage,
            canNextPage: canNextPage && !!next,
            totalCount: count,

            pageIndex,
            pageCount,
            pageSize,
            setPageSize,

            toggleAllRowsExpanded,

            label: "documents",
            setToLocalStorage: setPageSizeToStorage,
          }}
        />
      </div>
    </div>
  );
};

export default DocumentsColumns(FilterableDocumentsTable, {
  displayCaseInfo: true,
});
